import { SdkContext } from '../internal'
import type { AltchaChallenge } from '@/types/surveys'
import type {
  Form, FormAnswerInput, FormFill, FormListItem, FormPaged, FormSpaceConfig, FormSpaceMode, FormStatus,
  FormSubmission, FormSubmissionFilter, FormSubmissionStatus, FormSubmitted, FormWriteBody
} from '@/types/forms'

const pagedOf = <T,>(result: unknown): FormPaged<T> => {
  if (Array.isArray(result)) return { items: result as T[] }
  const r = result as FormPaged<T> | null
  return { items: r?.items || [], pagination: r?.pagination }
}

/** Only the filters that carry a value travel. */
const filterQuery = (filter: FormSubmissionFilter = {}) =>
  Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== undefined && v !== null && v !== ''))

/**
 * F-109 forms. The community routes take the community from X-CommunityId;
 * the public link (`/public/forms/{token}`) takes it from the token.
 */
export class FormsResource {
  constructor(private ctx: SdkContext) {}

  /** Managers get every form they manage (with counts); anyone else the open ones of the spaces they open. */
  async list(params: { spaceId?: string; status?: FormStatus; page?: number; limit?: number } = {}): Promise<FormPaged<FormListItem>> {
    return pagedOf<FormListItem>(await this.ctx.raw({ httpMethod: 'GET', path: '/forms' }, { query: params }))
  }

  async getSpace(spaceId: string): Promise<FormSpaceConfig> {
    return this.ctx.call({ httpMethod: 'GET', path: '/forms/spaces/{spaceId}' }, { spaceId })
  }

  async updateSpace(spaceId: string, mode: FormSpaceMode): Promise<{ spaceId: string; mode: FormSpaceMode }> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/forms/spaces/{spaceId}', hasBody: true }, { spaceId, body: { mode } })
  }

  async get(id: string): Promise<Form> {
    return this.ctx.call({ httpMethod: 'GET', path: '/forms/{id}' }, { id })
  }

  async create(body: FormWriteBody): Promise<Form> {
    return this.ctx.call({ httpMethod: 'POST', path: '/forms', hasBody: true }, { body })
  }

  /** `sections`, when sent, is the whole tree (replaced in one transaction). */
  /** `updatedAt`: the version read (409 with `data.updatedAt` when another save came first). */
  async update(id: string, body: FormWriteBody & { updatedAt?: string }): Promise<Form> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/forms/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/forms/{id}' }, { id })
  }

  async publish(id: string, notifyByEmail?: boolean): Promise<Form> {
    return this.ctx.call({ httpMethod: 'POST', path: '/forms/{id}/publish', hasBody: true }, { id, body: notifyByEmail === undefined ? {} : { notifyByEmail } })
  }

  async close(id: string): Promise<Form> {
    return this.ctx.call({ httpMethod: 'POST', path: '/forms/{id}/close', hasBody: true }, { id, body: {} })
  }

  async reopen(id: string): Promise<Form> {
    return this.ctx.call({ httpMethod: 'POST', path: '/forms/{id}/reopen', hasBody: true }, { id, body: {} })
  }

  /** The member's screen, inside the app (no password, no ALTCHA). */
  async fill(id: string): Promise<FormFill> {
    return this.ctx.call({ httpMethod: 'GET', path: '/forms/{id}/fill' }, { id })
  }

  async submit(id: string, answers: FormAnswerInput[]): Promise<FormSubmitted> {
    return this.ctx.call({ httpMethod: 'POST', path: '/forms/{id}/submissions', hasBody: true }, { id, body: { answers } })
  }

  async submissions(id: string, filter: FormSubmissionFilter = {}, page: { page?: number; limit?: number } = {}): Promise<FormPaged<FormSubmission>> {
    return pagedOf<FormSubmission>(await this.ctx.raw({ httpMethod: 'GET', path: '/forms/{id}/submissions' }, { id, query: { ...filterQuery(filter), ...page } }))
  }

  async submission(id: string, submissionId: string): Promise<FormSubmission> {
    return this.ctx.call({ httpMethod: 'GET', path: '/forms/{id}/submissions/{submissionId}' }, { id, submissionId })
  }

  async updateSubmission(id: string, submissionId: string, status: FormSubmissionStatus): Promise<FormSubmission> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/forms/{id}/submissions/{submissionId}', hasBody: true }, { id, submissionId, body: { status } })
  }

  async removeSubmission(id: string, submissionId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/forms/{id}/submissions/{submissionId}' }, { id, submissionId })
  }

  /** The CSV of the current filter as text, with the BOM put back (`fetch` drops it). */
  async exportCsv(id: string, filter: FormSubmissionFilter = {}): Promise<string> {
    const result = await this.ctx.raw<unknown>({ httpMethod: 'GET', path: '/forms/{id}/submissions/export' }, { id, query: filterQuery(filter) })
    const text = typeof result === 'string' ? result : String(result ?? '')
    return text.startsWith('﻿') ? text : '﻿' + text
  }

  /** Public link, no login needed; a session of the form's community fills in as a member. */
  async getPublic(publicToken: string): Promise<FormFill> {
    return this.ctx.call({ httpMethod: 'GET', path: '/public/forms/{publicToken}' }, { publicToken })
  }

  /**
   * The access password: the form and an `accessToken` (2 hours) for the submission.
   * `altcha` (SEC-217): a solved challenge of `getPublicChallenge`, when the form asks for one (`challengeRequired`).
   */
  async unlockPublic(publicToken: string, password: string, altcha?: string): Promise<FormFill> {
    return this.ctx.call(
      { httpMethod: 'POST', path: '/public/forms/{publicToken}/access', hasBody: true },
      { publicToken, body: altcha ? { password, altcha } : { password } }
    )
  }

  async getPublicChallenge(publicToken: string): Promise<AltchaChallenge> {
    return this.ctx.call({ httpMethod: 'GET', path: '/public/forms/{publicToken}/challenge' }, { publicToken })
  }

  async submitPublic(
    publicToken: string,
    body: { answers: FormAnswerInput[]; altcha?: string; accessToken?: string; website?: string }
  ): Promise<FormSubmitted> {
    return this.ctx.call({ httpMethod: 'POST', path: '/public/forms/{publicToken}/submissions', hasBody: true }, { publicToken, body })
  }
}
